import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
  forwardRef,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type CheckboxSize = 'sm' | 'md' | 'lg';
export type CheckboxVariant = 'checkbox' | 'switch';

@Component({
  selector: 'app-atom-checkbox',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
})
export class CheckboxComponent implements ControlValueAccessor {
  // Signal-based inputs
  disabledInput = input(false, { alias: 'disabled' });
  private _disabledFromCVA = signal<boolean>(false);
  disabled = computed(() => this.disabledInput() || this._disabledFromCVA());
  label = input<string>('');
  description = input<string>('');
  id = input<string>('');
  name = input<string>('');
  size = input<CheckboxSize>('md');
  variant = input<CheckboxVariant>('checkbox');
  additionalClasses = input<string>('');

  // Signal-based output
  checkedChange = output<boolean>();

  // Internal state
  internalChecked = signal<boolean>(false);

  // ControlValueAccessor callbacks
  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  // Computed checkbox classes
  checkboxClasses = computed(() => {
    const classes: string[] = [
      'text-primary-600',
      'border-2',
      'border-gray-300',
      'rounded',
      'focus:ring-primary-500',
      'focus:ring-2',
      'focus:ring-offset-0',
      'transition-colors',
      'duration-200',
    ];

    // Size classes
    const sizeMap: Record<CheckboxSize, string> = {
      sm: 'h-3.5 w-3.5',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
    };
    classes.push(sizeMap[this.size()]);

    if (this.disabled()) {
      classes.push('cursor-not-allowed', 'opacity-50', 'bg-gray-100');
    } else {
      classes.push('cursor-pointer', 'bg-white', 'hover:border-primary-500');
    }

    if (this.additionalClasses()) {
      classes.push(this.additionalClasses());
    }

    return classes.join(' ');
  });

  // Computed label classes
  labelClasses = computed(() => {
    const classes: string[] = ['font-medium', 'text-gray-700'];

    const sizeMap: Record<CheckboxSize, string> = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    };
    classes.push(sizeMap[this.size()]);

    if (this.disabled()) {
      classes.push('cursor-not-allowed', 'opacity-50');
    } else {
      classes.push('cursor-pointer');
    }

    return classes.join(' ');
  });

  // Computed description classes
  descriptionClasses = computed(() => {
    return 'text-xs text-gray-500 mt-0.5';
  });

  // ControlValueAccessor implementation
  writeValue(value: boolean): void {
    this.internalChecked.set(value ?? false);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
  }

  onCheckChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const checked = target.checked;

    if (!this.disabled()) {
      this.internalChecked.set(checked);
      this.onChange(checked);
      this.onTouched();
      this.checkedChange.emit(checked);
    }
  }
}
